import {
  BackComponent,
  ContactExpert,
  Loader,
  PageMainContent,
  PageWidgetContainer,
  Spinner,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { LinkIcon } from '@/icons/LinkIcon'
import { useBankingIntegrationSnackbar } from '@/modules/integrations/hooks/useBankingIntegrationSnackbar'
import { Redirect } from '@/routes/Redirect'
import { paths } from '@/routes/paths'
import { useGoToPath } from '@/utils'
import { useBankingIntegration } from '@/utils/hooks/useBankingIntegration'
import { Button } from '@surein/ui'
import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { makeBankTranslation } from '../helpers'
import {
  IntegratedBankAccountContainer,
  IntegratedBankAccountData,
  IntegratedScreenWrapper,
  IntegrationsButtonContainer,
  PageHeader,
  PageTitle,
} from '../styles'

// TODO: may need more work here but this section is unused currently and may change in the future
export interface BankingReports {
  general: {
    bankName: string
    iban: string
  }
}

interface OutletContext {
  hasProtectedAccess: boolean
}

export interface BankingIntegrationConfig {
  source: string
}

type SupportedLanguages = 'en' | 'de'

const config: BankingIntegrationConfig = {
  source: 'profile-integrations',
}

export const ProfileIntegrations: React.FC = () => {
  const { hasProtectedAccess } = useOutletContext<OutletContext>()
  const goToDashboard = useGoToPath('/dashboard')
  const { t, i18n } = useTranslation()
  const {
    redirectUserToTink,
    canGenerateTinkLink,
    reports,
    isLoading: isBankingIntegrationLoading,
  } = useBankingIntegration(config)

  const bankTranslation = (reports as BankingReports)?.general?.bankName
    ? makeBankTranslation(
        i18n.language as SupportedLanguages,
        (reports as BankingReports).general.bankName,
      )
    : ''

  const handleButtonClick = () => {
    redirectUserToTink()
  }

  // Encapsulates the logic to show a snackbar message when the banking integration is aborted or succeeded
  useBankingIntegrationSnackbar()

  if (isBankingIntegrationLoading) return <Loader />

  if (!hasProtectedAccess) return <Redirect to={paths.authRedirect} />

  if (reports && !isEmpty(reports)) {
    return (
      <>
        <PageMainContent>
          <BackComponent />
          <PageTitle>
            <PageHeader>
              <Typography variant="h4">
                {t('integrationsPage.integrations')}
              </Typography>
            </PageHeader>
          </PageTitle>
          <IntegratedScreenWrapper>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {t('integrationsPage.currentConnectedAccount', {
                bank: bankTranslation,
              })}
            </Typography>
            <IntegratedBankAccountContainer>
              <LinkIcon height="18" width="18" />
              <IntegratedBankAccountData>
                <Typography variant="p1Body">
                  {(reports as BankingReports).general.bankName}
                </Typography>
                <Typography variant="p1Body">
                  {(reports as BankingReports).general.iban}
                </Typography>
              </IntegratedBankAccountData>
            </IntegratedBankAccountContainer>
            <IntegrationsButtonContainer>
              <Button
                onClick={() => goToDashboard()}
                variant="primary"
                shape="square"
              >
                {t('back_to_dashboard')}
              </Button>
            </IntegrationsButtonContainer>
          </IntegratedScreenWrapper>
        </PageMainContent>
        <PageWidgetContainer>
          <ContactExpert text={t('recommend_advisor_text')} />
        </PageWidgetContainer>
      </>
    )
  }
  if (canGenerateTinkLink) {
    return (
      <>
        <PageMainContent>
          <BackComponent />
          <PageTitle>
            <PageHeader>
              <Typography variant="h4">
                {t('integrationsPage.integrations')}
              </Typography>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {t('integrationsPage.connectDescription')}
              </Typography>
            </PageHeader>
          </PageTitle>
          <IntegrationsButtonContainer>
            <Button
              variant="primary"
              shape="square"
              onClick={handleButtonClick}
            >
              {t('integrationsPage.connectButtonText')}
            </Button>
          </IntegrationsButtonContainer>
        </PageMainContent>
        <PageWidgetContainer>
          <ContactExpert text={t('recommend_advisor_text')} />
        </PageWidgetContainer>
      </>
    )
  }
  return (
    <>
      <PageMainContent>
        <BackComponent />
        <PageTitle>
          <PageHeader>
            <Typography variant="h4">
              {t('integrationsPage.integrations')}
            </Typography>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {t('integrationsPage.connectDescription')}
            </Typography>
          </PageHeader>
          <IntegrationsButtonContainer>
            <Button disabled icon={Spinner} variant="primary" shape="square">
              {t('integrationsPage.processingConnection')}
            </Button>
          </IntegrationsButtonContainer>
        </PageTitle>
      </PageMainContent>
      <PageWidgetContainer>
        <ContactExpert text={t('recommend_advisor_text')} />
      </PageWidgetContainer>
    </>
  )
}
